import { useQuery } from '@tanstack/react-query'
import { formatarDecimal, percentualPpm, ROTULO_POLITICA_PRECO, ROTULO_SENSIBILIDADE, type ExemploVenda, type PrecoCaixaGrupo, type ResultadoHibrido, type SimulacaoResultado } from '@via-tax/contracts'
import { Aviso, Cartao, EstadoVazio, Selo } from '@via-tax/ui'
import { useState } from 'react'
import { ComoFuncionaHibrido } from '../components/ComoFuncionaHibrido'
import { RespostaHibrido } from '../components/RespostaHibrido'
import { useExecutarSimulacao } from '../components/ExecutarSimulacao'
import type { Tela } from '../components/Shell'
import { dataBr, reais, reaisCompacto } from '../formatos'
import type { ContextoSessao } from '../tipos'
import { useTRPC } from '../trpc'

/**
 * Simples: puro × híbrido (arquitetura §11). A decisão que o optante do Simples precisa tomar
 * antes de 2027: continuar com IBS/CBS dentro do DAS ou apurar pelo regime regular
 * (LC 123, art. 13 §§ 9º e 10). Mesmo resultado imutável da simulação do Foco 2027.
 */

/** ppm → "29,04%" */
const pct = percentualPpm
/** ppm → "3,45%", sempre com duas casas */
const pctFixo = (ppm: number) => `${formatarDecimal(Math.abs(ppm), 4)}%`
const pp = (ppm: number) => `${(Math.abs(ppm) / 10_000).toFixed(1).replace('.', ',')} p.p.`
const sinal = (centavos: number) => (centavos > 0 ? `+${reais(centavos)}` : reais(centavos))
const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro']
const mesExtenso = (aaaaMm: string) => `${MESES[Number(aaaaMm.slice(5, 7)) - 1]} de ${aaaaMm.slice(0, 4)}`
const dataIso = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`
const mesAno = (c: string) => `${c.slice(5, 7)}/${c.slice(2, 4)}`

export function SimplesHibrido({ contexto, ir }: { contexto: ContextoSessao; ir: (t: Tela) => void }) {
  const trpc = useTRPC()
  const simples = contexto.empresaAtiva?.regimeTributario === 'simples_nacional'
  const podeExecutar = (contexto.capacidades as string[]).includes('executar_simulacao')
  const ultima = useQuery({
    ...trpc.simulacao.ultima.queryOptions(),
    enabled: simples,
    refetchInterval: (q) => (q.state.data?.recente?.status === 'PROCESSANDO' ? 3000 : false),
  })
  const recente = ultima.data?.recente
  const concluida = ultima.data?.concluida
  const processando = recente?.status === 'PROCESSANDO'
  const execucao = useExecutarSimulacao({ premissasAtuais: concluida?.premissas ?? recente?.premissas, processando, jaRodou: !!concluida, simples: true, operacoes: concluida?.resultado.operacoes })
  const hibrido = concluida?.resultado.hibrido

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Simples: puro × híbrido</h1>
          <p className="mt-1 max-w-2xl text-sm text-tinta-2">
            Continuar com IBS e CBS dentro do DAS, ou apurá-los por fora para dar crédito cheio ao cliente empresa e creditar as próprias compras. Simulação para decisão — não é
            apuração.
          </p>
        </div>
        {simples && podeExecutar && execucao.botoes}
      </header>

      {!simples ? (
        <EstadoVazio titulo="Esta análise é para empresas do Simples Nacional">
          O regime desta empresa no cadastro não é o Simples. Se estiver errado, corrija no Processos ou em Dados da empresa.
        </EstadoVazio>
      ) : (
        <>
          {podeExecutar && execucao.painel}
          {!hibrido?.[0]?.zona && <ComoFuncionaHibrido h={hibrido?.[0] ?? null} />}
          {processando && <Aviso tom="info">Calculando — a tela atualiza sozinha.</Aviso>}
          {recente?.status === 'ERRO' && <Aviso>A última simulação não terminou: {recente.erro}</Aviso>}
          {ultima.isPending ? (
            <p className="text-sm text-tinta-3">Carregando…</p>
          ) : !hibrido?.length ? (
            !processando && (
              <EstadoVazio titulo={concluida ? 'A última simulação é anterior a esta análise' : 'Nenhuma simulação ainda'}>
                {podeExecutar
                  ? 'Rode a simulação: a comparação puro × híbrido sai junto com o Foco 2027, a partir das notas dos últimos 12 meses.'
                  : 'O escritório ainda não rodou esta análise para a empresa.'}
              </EstadoVazio>
            )
          ) : (
            <>
              <Comparacao anos={hibrido} r={concluida!.resultado} meta={{ criadoPor: concluida!.criadoPor, criadoEm: concluida!.criadoEm }} ir={ir} staff={podeExecutar} />
              {hibrido[0]?.zona && <ComoFuncionaHibrido h={hibrido[0]} />}
            </>
          )}
        </>
      )}
    </div>
  )
}

function Comparacao({ anos, r, meta, ir, staff }: { anos: ResultadoHibrido[]; r: SimulacaoResultado; meta: { criadoPor: string; criadoEm: string | Date }; ir: (t: Tela) => void; staff: boolean }) {
  const [ano, setAno] = useState(anos[0]!.ano)
  const h = anos.find((x) => x.ano === ano) ?? anos[0]!
  const hoje = new Date().toISOString().slice(0, 7)
  const proxima = h.opcao.semestres.find((s) => s.mesOpcao >= hoje)
  const indicaHibrido = h.recomendacao === 'HIBRIDO'
  const viram = h.sensibilidade.filter((s) => s.vira)

  const detalhes = (
    <>
        <section className="rounded-lg border border-linha bg-painel p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-xs text-tinta-3">Indicação para {ano} com o repasse das premissas · cenário base (CBS {pct(h.aliquotas.cbs)} + IBS {pct(h.aliquotas.ibs)})</p>
              <p className="mt-1 text-2xl font-semibold">{indicaHibrido ? 'Optar pelo Simples híbrido' : 'Permanecer no Simples puro'}</p>
              <p className="mt-1 text-sm text-tinta-2">
                {indicaHibrido ? 'O híbrido deixa' : 'O puro deixa'} <strong className="num text-tinta">{reais(Math.abs(h.diferenca))}</strong> a mais para a empresa no ano.
              </p>
            </div>
            <div className="flex flex-col items-end gap-1">
              {h.estavel ? <Selo tom="ok">recomendação estável</Selo> : <Selo tom="alerta">recomendação instável</Selo>}
              {h.aliquotas.status === 'ESTIMATIVA' && <Selo tom="neutro">alíquota estimada</Selo>}
            </div>
          </div>
          <FraseVirada h={h} />
          {!h.estavel && (
            <p className="mt-3 text-sm text-alerta">
              A indicação vira do avesso em: {viram.map((s) => ROTULO_SENSIBILIDADE[s.fator].toLowerCase()).join('; ')}. Antes de decidir, confirme esses pontos com o cliente.
            </p>
          )}
        </section>

        {h.aPagar && <QuantoPaga h={h} />}
        {h.precoCaixa && <PrecoCaixa h={h} />}
        {h.porGrupo.some((g) => g.ganho !== undefined) && <OndeCompensa h={h} />}
        {h.exemploPreco && h.politicasPreco && <PrecoDeVenda h={h} />}

        <Cartao titulo="Os dois lados da conta (ano)">
          <Lados h={h} dasHoje={r.resultados.find((x) => x.ano === h.ano && x.cenario === 'BASE')?.atual.das ?? null} />
        </Cartao>
        <Cartao titulo="Preço para o cliente">
          <Precos h={h} />
        </Cartao>

        {h.porCompetencia && h.porCompetencia.length > 0 && (
          <Cartao titulo={`Mês a mês com as alíquotas de ${h.ano}`}>
            <MesAMes h={h} />
          </Cartao>
        )}

        {anos.length > 1 && anos.every((a) => a.aPagar) && (
          <Cartao titulo={`Trajetória ${anos[0]!.ano}–${anos[anos.length - 1]!.ano}: puro × híbrido ano a ano`}>
            <Trajetoria anos={anos} />
          </Cartao>
        )}

        <Cartao titulo="Sensibilidade: o que mexe na decisão">
          <Tornado h={h} />
        </Cartao>
    </>
  )

  return (
    <div className="flex flex-col gap-5">
      {proxima && (
        <Aviso tom={proxima.mesOpcao === hoje ? 'alerta' : 'info'}>
          A opção pelo híbrido para o semestre que começa em {mesExtenso(proxima.inicio)} é feita em <strong>{mesExtenso(proxima.mesOpcao)}</strong>
          {proxima.cancelamentoAte ? (
            <>
              {' '}e pode ser cancelada até <strong>{dataIso(proxima.cancelamentoAte)}</strong>, antes de o semestre começar
            </>
          ) : null}
          . Depois disso vale até a empresa renunciar, nas mesmas janelas de opção.{' '}
          <span className="text-xs">{h.opcao.fundamentoCancelamento ?? h.opcao.fundamento.split(';')[0]}.</span>
        </Aviso>
      )}

      {anos.length > 1 && (
        <div role="tablist" className="inline-flex w-fit rounded-md border border-linha bg-painel p-0.5">
          {anos.map((a) => (
            <button key={a.ano} role="tab" aria-selected={a.ano === ano} onClick={() => setAno(a.ano)} className={`rounded px-3 py-1.5 text-sm ${a.ano === ano ? 'bg-navy text-white' : 'text-tinta-2 hover:text-tinta'}`}>
              {a.ano}
            </button>
          ))}
        </div>
      )}

      {h.zona ? (
        <>
          <RespostaHibrido key={h.ano} h={h} z={h.zona} />
          <details className="rounded-lg border border-linha bg-painel">
            <summary className="cursor-pointer select-none px-5 py-3 text-sm font-semibold text-navy">
              Detalhes do cálculo <span className="font-normal text-tinta-3">· guias, caixa, mês a mês, trajetória e sensibilidade, com o repasse das premissas</span>
            </summary>
            <div className="flex flex-col gap-5 border-t border-linha p-5">{detalhes}</div>
          </details>
        </>
      ) : (
        <>
          <Aviso tom="info">Esta simulação é de uma versão anterior do motor. Rode de novo para ver a resposta com a faixa de preço para o cliente empresa.</Aviso>
          {detalhes}
        </>
      )}

      <Cartao titulo="Antes de decidir">
        <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm">
          {h.creditoDependePremissa > 0 && (
            <li>
              {reais(h.creditoDependePremissa)} do crédito das compras vem de fornecedor com regime presumido. Confirme os maiores.{' '}
              {staff && (
                <button className="text-navy underline" onClick={() => ir('fornecedores')}>
                  abrir
                </button>
              )}
            </li>
          )}
          <li>
            Confirme com o cliente quanto das vendas vai para empresa que se credita (hoje {pct(h.mix.atual, 1)} pelas notas).{' '}
            {staff && (
              <button className="text-navy underline" onClick={() => ir('clientes')}>
                abrir
              </button>
            )}
          </li>
          {r.qualidade.receitaSemClassificacao > 0 && (
            <li>
              {pct(r.qualidade.receitaSemClassificacao, 1)} da receita ainda não tem classificação aprovada — reduções de alíquota mudam o preço de equilíbrio.{' '}
              {staff && (
                <button className="text-navy underline" onClick={() => ir('produtos')}>
                  abrir
                </button>
              )}
            </li>
          )}
          <li>O híbrido exige apurar IBS/CBS todo mês e destacar na nota: combine a rotina e o custo com o escritório.</li>
        </ol>
        {r.qualidade.avisos.length > 0 && (
          <ul className="mt-4 list-disc space-y-1 pl-5 text-xs text-tinta-3">
            {r.qualidade.avisos.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        )}
      </Cartao>

      <p className="text-xs text-tinta-3">
        Motor {r.proveniencia.versaoEngine} · parâmetros {r.proveniencia.versaoParametros} · calculado em {dataBr(r.proveniencia.calculadoEm)} · pedido por {meta.criadoPor} em{' '}
        {dataBr(meta.criadoEm)}
      </p>
    </div>
  )
}

/** "Hoje X% das suas vendas vão para empresas que se creditam. O híbrido passa a compensar a partir de Y%." (§11.4) */
function FraseVirada({ h }: { h: ResultadoHibrido }) {
  const { atual, pontoVirada, semViradaHibridoGanha } = h.mix
  const inicio = `Hoje ${pct(atual, 0)} das vendas vão para empresas que se creditam.`
  let texto: string
  if (pontoVirada === null) {
    texto = semViradaHibridoGanha ? `${inicio} Com estas compras e premissas, o híbrido compensa em qualquer mix.` : `${inicio} Com estas compras e premissas, o puro compensa em qualquer mix.`
  } else {
    const folga = atual - pontoVirada
    texto = `${inicio} O híbrido passa a compensar a partir de ${pct(pontoVirada, 0)}. Você está ${pp(folga)} ${folga >= 0 ? 'acima' : 'abaixo'} do ponto de virada${
      Math.abs(folga) < 50_000 ? ' — perto demais para decidir sem confirmar o mix' : ''
    }.`
  }
  return (
    <div className="mt-4 flex flex-col gap-2">
      <p className="text-sm">{texto}</p>
      <div className="relative h-3 rounded bg-fundo" aria-hidden>
        {pontoVirada !== null && <div className="absolute inset-y-0 right-0 rounded-r bg-ok/20" style={{ left: `${pontoVirada / 10_000}%` }} />}
        {pontoVirada !== null && <div className="absolute -inset-y-1 w-0.5 bg-tinta-3" style={{ left: `${pontoVirada / 10_000}%` }} title="ponto de virada" />}
        <div className="absolute -inset-y-1.5 w-1.5 -translate-x-1/2 rounded bg-navy" style={{ left: `${atual / 10_000}%` }} title="mix atual" />
      </div>
      <div className="flex justify-between text-[11px] text-tinta-3">
        <span>0% para quem se credita</span>
        {pontoVirada !== null && <span>faixa verde: híbrido compensa</span>}
        <span>100%</span>
      </div>
    </div>
  )
}

/**
 * Os dois lados da conta com o valor de venda de HOJE como referência: o cliente enxerga o que vende
 * hoje, o que passa a sair na nota em cada regime, o que paga de guia e o que sobra.
 * Híbrido: preço + débito − DAS − (débito − crédito) = preço − DAS + crédito (fecha com o resultado).
 */
function Lados({ h, dasHoje }: { h: ResultadoHibrido; dasHoje: number | null }) {
  const { puro, hibrido } = h
  const ap = h.aPagar
  const cbsIbsRecolher = ap ? ap.hibrido.cbs + ap.hibrido.ibs : hibrido.debitoCbsIbs - hibrido.creditoEntradas
  const variacaoPreco = puro.receita ? Math.round(((hibrido.receita - puro.receita) * 1_000_000) / puro.receita) : 0
  // No Simples o DAS de hoje e o do puro costumam ser iguais: painel "Hoje" separado só quando diferem.
  const comHoje = dasHoje !== null && dasHoje !== puro.das
  const lado = (v: { vendas: number; preco: number; tributo: number; das: number; recolher: number; fica: number }, dicas: Partial<Record<'preco' | 'das' | 'recolher', string>> = {}): LinhaPainel[] => [
    { rotulo: 'Vendas: total das notas', valor: v.vendas, forte: true },
    { rotulo: 'Preço dos produtos', valor: v.preco, dica: dicas.preco, recuo: true },
    { rotulo: 'IBS/CBS destacado na nota', valor: v.tributo, recuo: true },
    { rotulo: '(−) DAS', valor: -v.das, dica: dicas.das },
    { rotulo: '(−) CBS e IBS a recolher', valor: -v.recolher, dica: dicas.recolher },
  ]
  const paineis: PainelProps[] = [
    ...(comHoje
      ? [{ titulo: 'Hoje', linhas: lado({ vendas: puro.receita, preco: puro.receita, tributo: 0, das: dasHoje!, recolher: 0, fica: 0 }), total: puro.receita - dasHoje! }]
      : []),
    {
      titulo: comHoje ? `Simples puro ${h.ano}` : `Hoje e Simples puro ${h.ano}`,
      subtitulo: comHoje ? undefined : 'mesmo valor: a alíquota do DAS não muda',
      linhas: lado({ vendas: puro.receita, preco: puro.receita, tributo: 0, das: puro.das, recolher: 0, fica: puro.resultado }),
      total: puro.resultado,
    },
    {
      titulo: `Simples híbrido ${h.ano}`,
      destaque: true,
      linhas: lado(
        { vendas: hibrido.receita + hibrido.debitoCbsIbs, preco: hibrido.receita, tributo: hibrido.debitoCbsIbs, das: hibrido.das, recolher: cbsIbsRecolher, fica: hibrido.resultado },
        {
          preco: variacaoPreco === 0 ? 'igual a hoje' : `${variacaoPreco > 0 ? '+' : '−'}${pct(Math.abs(variacaoPreco), 1)} contra hoje`,
          das: 'sem a parcela de CBS/IBS',
          recolher: `débito ${reaisCompacto(hibrido.debitoCbsIbs)} − crédito das compras ${reaisCompacto(hibrido.creditoEntradas)}`,
        },
      ),
      total: hibrido.resultado,
    },
  ]
  const ganhaHibrido = h.diferenca > 0
  return (
    <div className="flex flex-col gap-4">
      <div className={`grid gap-4 ${comHoje ? 'lg:grid-cols-3' : 'md:grid-cols-2'}`}>
        {paineis.map((x) => (
          <Painel key={x.titulo} {...x} />
        ))}
      </div>
      <div className={`flex flex-wrap items-baseline justify-between gap-2 rounded-md px-4 py-3 ${ganhaHibrido ? 'bg-ok/10' : 'bg-alerta/10'}`}>
        <span className="text-sm font-medium">{ganhaHibrido ? 'O híbrido deixa mais para a empresa' : 'O puro deixa mais para a empresa'}</span>
        <span className={`num text-lg font-semibold ${ganhaHibrido ? 'text-ok' : 'text-alerta'}`}>{reais(Math.abs(h.diferenca))} por ano</span>
      </div>
      <p className="text-xs text-tinta-3">
        No híbrido o IBS/CBS é cobrado do cliente na nota e o crédito das compras abate o que se recolhe; por isso &quot;fica para a empresa&quot; = preço dos produtos − DAS + crédito das
        compras.
      </p>
    </div>
  )
}

type LinhaPainel = { rotulo: string; valor: number; dica?: string; forte?: boolean; recuo?: boolean }
type PainelProps = { titulo: string; subtitulo?: string; destaque?: boolean; linhas: LinhaPainel[]; total?: number; rotuloTotal?: string }

/** Coluna de valores com o rótulo em cada linha: quebra no celular sem barra de rolagem. */
function Painel({ titulo, subtitulo, destaque, linhas, total, rotuloTotal = 'Fica para a empresa' }: PainelProps) {
  return (
    <div className={`flex min-w-0 flex-col rounded-md border p-3 ${destaque ? 'border-navy/40 bg-navy/[0.03]' : 'border-linha'}`}>
      <p className={`text-sm font-semibold ${destaque ? 'text-navy' : ''}`}>{titulo}</p>
      {subtitulo && <p className="text-[11px] text-tinta-3">{subtitulo}</p>}
      <dl className="mt-2 flex flex-col">
        {linhas.map((l) => (
          <div key={l.rotulo} className={`flex items-baseline justify-between gap-3 border-t border-linha py-1.5 text-sm ${l.forte ? 'font-semibold' : ''}`}>
            <dt className={`min-w-0 ${l.recuo ? 'pl-3' : ''}`}>
              {l.rotulo}
              {l.dica && <span className="block text-[11px] font-normal text-tinta-3">{l.dica}</span>}
            </dt>
            <dd className="num shrink-0 whitespace-nowrap text-right">{reais(l.valor)}</dd>
          </div>
        ))}
        {total !== undefined && (
          <div className="flex items-baseline justify-between gap-3 border-t-2 border-linha pt-2 text-sm font-semibold">
            <dt className="min-w-0">{rotuloTotal}</dt>
            <dd className="num shrink-0 whitespace-nowrap text-right">{reais(total)}</dd>
          </div>
        )}
      </dl>
    </div>
  )
}

const ROTULO_GRUPO = { COM_CREDITO: 'Empresas que se creditam', SEM_CREDITO: 'Consumidor final e empresas sem crédito' } as const

function Precos({ h }: { h: ResultadoHibrido }) {
  return (
    <div className="flex flex-col gap-3">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-tinta-3">
            <th className="pb-2 font-medium">Vendas para</th>
            <th className="pb-2 text-right font-medium">Receita</th>
            <th className="pb-2 text-right font-medium">Preço sem tributo</th>
            <th className="pb-2 text-right font-medium">Cliente paga</th>
          </tr>
        </thead>
        <tbody>
          {h.porGrupo.map((g) => (
            <tr key={g.grupo} className="border-t border-linha">
              <td className="py-1.5">
                {ROTULO_GRUPO[g.grupo]}
                <span className="block text-[11px] text-tinta-3">IBS/CBS médio {pct(g.aliquotaMedia)}</span>
              </td>
              <td className="num py-1.5 text-right">{reaisCompacto(g.receita)}</td>
              <td className="num py-1.5 text-right">{pct(g.precoEquilibrio)}</td>
              <td className={`num py-1.5 text-right ${g.custoCliente > 0 ? 'text-alerta' : ''}`}>{Math.abs(g.custoCliente) < 100 ? 'o mesmo' : sinal(g.custoCliente)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-tinta-2">
        "Preço sem tributo" é quanto o preço de hoje pode mudar no híbrido sem o cliente pagar mais: quem se credita aceita pagar o que hoje paga menos o crédito que já recebe;
        quem não se credita paga o tributo por fora, então o preço sem tributo precisa cair. "Cliente paga" usa o repasse das premissas.
      </p>
    </div>
  )
}

function Tornado({ h }: { h: ResultadoHibrido }) {
  const valores = h.sensibilidade.flatMap((s) => [s.diferencaA, s.diferencaB]).concat(h.diferenca, 0)
  const min = Math.min(...valores)
  const max = Math.max(...valores)
  const x = (v: number) => (max === min ? 50 : ((v - min) / (max - min)) * 100)
  return (
    <div className="flex flex-col gap-2">
      {h.sensibilidade.map((s) => {
        const esq = x(Math.min(s.diferencaA, s.diferencaB))
        const larg = Math.max(0.8, x(Math.max(s.diferencaA, s.diferencaB)) - esq)
        return (
          <div key={s.fator} className="grid grid-cols-1 gap-1 text-sm md:grid-cols-[14rem_1fr] md:items-center md:gap-3">
            <span className="flex items-center gap-1.5">
              {ROTULO_SENSIBILIDADE[s.fator]} {s.vira && <Selo tom="alerta">vira</Selo>}
            </span>
            <div>
              <div className="relative h-5 rounded bg-fundo">
                <div className="absolute inset-y-0 rounded bg-navy/30" style={{ left: `${esq}%`, width: `${larg}%` }} />
                <div className="absolute -inset-y-1 w-px bg-alerta" style={{ left: `${x(0)}%` }} />
                <div className="absolute -inset-y-0.5 w-0.5 bg-navy" style={{ left: `${x(h.diferenca)}%` }} />
              </div>
              <div className="mt-0.5 flex justify-between gap-2 text-[11px] text-tinta-3">
                <span>
                  {s.rotuloA}: <span className="num">{sinal(s.diferencaA)}</span>
                </span>
                <span>
                  {s.rotuloB}: <span className="num">{sinal(s.diferencaB)}</span>
                </span>
              </div>
            </div>
          </div>
        )
      })}
      <p className="mt-1 text-xs text-tinta-3">
        Valores = quanto o híbrido deixa a mais (+) ou a menos (−) que o puro. Linha vermelha: empate; traço azul: cenário atual. Barra que cruza a linha vermelha muda a indicação.
      </p>
    </div>
  )
}

/** Guias do ano e o que sobra: guia maior no híbrido não quer dizer custo maior (o IBS/CBS vem na nota do cliente). */
function QuantoPaga({ h }: { h: ResultadoHibrido }) {
  const ap = h.aPagar!
  const entraHibrido = h.hibrido.receita + h.hibrido.debitoCbsIbs
  const cbsIbsRecolher = ap.hibrido.cbs + ap.hibrido.ibs
  const mes = (v: number) => `≈ ${reais(Math.round(v / 12))} por mês`
  return (
    <Cartao titulo={`Quanto vai pagar em ${h.ano} — e quanto sobra`}>
      <p className="mb-3 text-sm text-tinta-2">
        No híbrido a guia é maior porque o IBS/CBS passa a vir destacado na nota: o cliente paga junto com a compra e a empresa repassa ao fisco. Esse dinheiro entra e sai. O que
        decide é a última linha — quanto fica para a empresa depois de tudo.
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        <Painel
          titulo={`Simples puro ${h.ano}`}
          linhas={[
            { rotulo: 'Entra: total das notas', valor: h.puro.receita, forte: true },
            { rotulo: '(−) DAS no ano', valor: -h.puro.das, dica: mes(h.puro.das) },
          ]}
          total={h.puro.resultado}
        />
        <Painel
          titulo={`Simples híbrido ${h.ano}`}
          destaque
          linhas={[
            { rotulo: 'Entra: total das notas', valor: entraHibrido, forte: true },
            { rotulo: 'Preço dos produtos', valor: h.hibrido.receita, recuo: true },
            { rotulo: 'IBS/CBS cobrado do cliente', valor: h.hibrido.debitoCbsIbs, recuo: true },
            { rotulo: '(−) DAS no ano', valor: -ap.hibrido.das, dica: `${mes(ap.hibrido.das)} · sem a parcela de CBS/IBS` },
            {
              rotulo: '(−) CBS e IBS a recolher',
              valor: -cbsIbsRecolher,
              dica: `${mes(cbsIbsRecolher)} · já descontado o crédito das compras ${reaisCompacto(h.hibrido.creditoEntradas)}`,
            },
          ]}
          total={h.hibrido.resultado}
        />
      </div>
      <p className="mt-3 text-sm">
        Guias no ano: puro <strong className="num">{reais(ap.puro.total)}</strong> · híbrido <strong className="num">{reais(ap.hibrido.total)}</strong>. A diferença de{' '}
        <span className="num">{reais(ap.hibrido.total - ap.puro.total)}</span> não sai do bolso da empresa: dos <span className="num">{reais(ap.hibrido.total)}</span> do híbrido,{' '}
        <span className="num">{reais(h.hibrido.debitoCbsIbs)}</span> foi cobrado do cliente na nota. No fim, {h.diferenca > 0 ? 'o híbrido' : 'o puro'} deixa{' '}
        <strong className="num">{reais(Math.abs(h.diferenca))}</strong> a mais para a empresa.
      </p>
      <p className="mt-1 text-xs text-tinta-3">Vencimento no mês seguinte à competência, pelo calendário do Simples (DAS) e pelo regulamento do IBS/CBS.</p>
    </Cartao>
  )
}

/** variação de b contra a, em ppm */
const varPpm = (novo: number, velho: number) => (velho ? Math.round(((novo - velho) * 1_000_000) / velho) : 0)
const varTexto = (novo: number, velho: number) => {
  const v = varPpm(novo, velho)
  return Math.abs(v) < 500 ? 'igual a hoje' : `${v > 0 ? '+' : '\u2212'}${pctFixo(v)} contra hoje`
}

/**
 * Preço mínimo de venda (o piso que não tira dinheiro do caixa) e, depois dele, o ganho de manter
 * a nota de hoje. O cliente empresa decide pelo CUSTO dele, não pelo valor da nota: por isso o custo
 * real aparece em cada coluna.
 */
function PrecoCaixa({ h }: { h: ResultadoHibrido }) {
  const c = h.precoCaixa!
  const t = c.total
  const ganho = t.mesmaNota.sobra - t.sobraPuro
  const precoCai = varPpm(t.mesmaSobra.preco, t.receitaHoje)
  const soVenda = varPpm(t.semCreditoCompras.preco, t.receitaHoje)
  const comCredito = c.grupos.find((g) => g.grupo === 'COM_CREDITO')
  return (
    <Cartao titulo={`Preço mínimo de venda em ${h.ano} — e o ganho de manter a nota de hoje`}>
      <div className="flex flex-col gap-2 rounded-md bg-fundo p-4 text-sm">
        <p>
          <strong>1. O piso: o mínimo que dá para vender.</strong> Com o IBS/CBS por fora e o DAS menor, o preço do produto pode {precoCai < 0 ? 'cair' : 'subir'}{' '}
          <strong className="num">{pctFixo(precoCai)}</strong> sem tirar nada do caixa: um produto de {reais(100_000)} hoje pode sair por{' '}
          <strong className="num">{reais(Math.round(100_000 + (100_000 * precoCai) / 1_000_000))}</strong> + {pctFixo(t.aliquotaMedia)} de IBS/CBS ={' '}
          <strong className="num">{reais(Math.round(100_000 + (100_000 * t.aumento) / 1_000_000))}</strong> na nota. Não é somar {pctFixo(t.aliquotaMedia)} ao preço de hoje: isso seria
          manter o preço do produto e jogar o tributo por fora.
        </p>
        <p>
          <strong>2. O que o cliente sente nesse piso.</strong>{' '}
          {comCredito
            ? `Empresa que se credita: o custo dela ${varPpm(comCredito.mesmaSobra.custoCliente, comCredito.custoClienteHoje) < 0 ? 'cai' : 'sobe'} ${pctFixo(varPpm(comCredito.mesmaSobra.custoCliente, comCredito.custoClienteHoje))} — ela credita o IBS/CBS cheio da nota. `
            : ''}
          Consumidor final e empresa sem crédito pagam a nota inteira: {pctFixo(t.aumento)} {t.aumento > 0 ? 'a mais' : 'a menos'} que hoje.
        </p>
        <p>
          <strong>3. Só depois, o ganho de manter a nota de hoje.</strong> Vendendo pelo mesmo valor final de hoje, ficam{' '}
          <strong className="num">{reais(t.mesmaNota.sobra)}</strong> no ano — <span className={`num font-semibold ${ganho < 0 ? 'text-alerta' : 'text-ok'}`}>{sinal(ganho)}</span> contra
          os {reais(t.sobraPuro)} de hoje.
        </p>
      </div>
      <p className="mt-3 text-xs text-tinta-3">
        O piso conta com o crédito das compras ({reais(t.creditoCompras)} no ano), que entra no caixa e derruba o preço mínimo: olhando só a venda, sem esse crédito, o piso do
        produto ficaria {pctFixo(soVenda)} {soVenda < 0 ? 'abaixo' : 'acima'} do preço de hoje. Aqui o preço é formado de um jeito só para todos; o quadro de guias acima usa o repasse das premissas
        (empresas {pctFixo(h.repasseUsado?.COM_CREDITO ?? 0)}, consumidor {pctFixo(h.repasseUsado?.SEM_CREDITO ?? 0)}).
      </p>
      <div className="mt-4 flex flex-col gap-5">
        <BlocoPreco titulo="Todas as vendas" g={t} ano={h.ano} />
        {c.grupos.length > 1 && c.grupos.map((g) => <BlocoPreco key={g.grupo} titulo={ROTULO_GRUPO[g.grupo]} g={g} ano={h.ano} credita={g.grupo === 'COM_CREDITO'} />)}
      </div>
      <p className="mt-4 text-xs text-tinta-3">
        Conta do ano inteiro, com o mesmo volume de vendas e o mesmo custo de compra. Por tipo de cliente, o crédito das compras entra rateado pela participação dele na receita. O
        piso mantém o caixa do ano, não a margem de cada produto: item a item, o piso depende do crédito da compra daquele item.
      </p>
    </Cartao>
  )
}

function BlocoPreco({ titulo, g, ano, credita }: { titulo: string; g: PrecoCaixaGrupo; ano: number; credita?: boolean }) {
  const hoje = { nota: g.receitaHoje, preco: g.receitaHoje, tributo: 0, das: g.receitaHoje - g.sobraPuro, sobra: g.sobraPuro, custoCliente: g.custoClienteHoje }
  const linhas = (x: typeof hoje, inicial: boolean) => [
    { rotulo: 'Preço do produto', valor: x.preco, dica: inicial ? undefined : varTexto(x.preco, hoje.preco) },
    { rotulo: 'IBS/CBS na nota', valor: x.tributo },
    { rotulo: 'Valor final da nota', valor: x.nota, forte: true, dica: inicial ? undefined : varTexto(x.nota, hoje.nota) },
    { rotulo: credita === undefined ? 'O cliente paga' : 'Custo real do cliente', valor: x.custoCliente, forte: true, dica: inicial ? undefined : varTexto(x.custoCliente, hoje.custoCliente) },
    { rotulo: '(−) DAS', valor: -x.das },
    ...(inicial ? [] : [{ rotulo: '(+) crédito das compras', valor: g.creditoCompras }]),
  ]
  const unitario = (x: typeof hoje) => Math.round((100_000 * x.preco) / (g.receitaHoje || 1))
  return (
    <div>
      <p className="mb-2 text-sm font-medium">
        {titulo} <span className="text-xs font-normal text-tinta-3">· IBS/CBS médio {pctFixo(g.aliquotaMedia)}</span>
      </p>
      <div className="grid gap-3 sm:grid-cols-3">
        <Painel titulo="Hoje (Simples puro)" linhas={linhas(hoje, true)} total={g.sobraPuro} />
        <Painel
          titulo={`Híbrido ${ano}: preço mínimo`}
          subtitulo="o piso que mantém o caixa"
          destaque
          linhas={linhas(g.mesmaSobra, false)}
          total={g.mesmaSobra.sobra}
        />
        <Painel titulo={`Híbrido ${ano}: mesma nota de hoje`} subtitulo="o cliente paga o mesmo total" linhas={linhas(g.mesmaNota, false)} total={g.mesmaNota.sobra} />
      </div>
      <p className="mt-1.5 text-xs text-tinta-2">
        Um produto que hoje sai por {reais(100_000)}: no preço mínimo o produto fica em <span className="num">{reais(unitario(g.mesmaSobra))}</span> e a nota em{' '}
        <span className="num">{reais(Math.round((100_000 * g.mesmaSobra.nota) / (g.receitaHoje || 1)))}</span>
        {credita === true
          ? `; como o cliente credita o IBS/CBS, a compra custa a ele ${reais(unitario(g.mesmaSobra))} — ${varTexto(g.mesmaSobra.custoCliente, g.custoClienteHoje)}.`
          : credita === false
            ? `; esse cliente não credita nada e paga a nota inteira — ${varTexto(g.mesmaSobra.nota, g.receitaHoje)}.`
            : '.'}
      </p>
    </div>
  )
}

/** Cada parte da operação empurra a decisão para um lado; a soma é a diferença do ano. */
function OndeCompensa({ h }: { h: ResultadoHibrido }) {
  const com = h.porGrupo.find((g) => g.grupo === 'COM_CREDITO')
  const sem = h.porGrupo.find((g) => g.grupo === 'SEM_CREDITO')
  const itens: Array<[string, number, string]> = []
  if (com) itens.push([`Vendas a empresas que se creditam (${reaisCompacto(com.receita)})`, com.ganho ?? 0, 'O cliente passa a creditar o IBS/CBS cheio da nota; o DAS dessa venda cai.'])
  if (sem) itens.push([`Vendas a consumidor final e empresas sem crédito (${reaisCompacto(sem.receita)})`, sem.ganho ?? 0, 'O tributo vai por fora e o cliente não credita: para ele pagar o mesmo, o preço sem tributo cai.'])
  itens.push(['Crédito das compras', h.creditoCompras ?? h.hibrido.creditoEntradas, 'Só o híbrido credita o IBS/CBS do que compra.'])
  return (
    <Cartao titulo="Onde o híbrido compensa — e onde não">
      <div className="flex flex-col gap-2">
        {itens.map(([rotulo, valor, dica]) => (
          <div key={rotulo} className={`flex items-start justify-between gap-3 rounded-md border-l-4 bg-fundo px-3 py-2 ${valor > 0 ? 'border-ok' : valor < 0 ? 'border-alerta' : 'border-linha'}`}>
            <div>
              <p className="text-sm font-medium">{rotulo}</p>
              <p className="text-xs text-tinta-3">{dica}</p>
            </div>
            <span className={`num shrink-0 text-sm font-semibold ${valor > 0 ? 'text-ok' : valor < 0 ? 'text-alerta' : ''}`}>{sinal(valor)}</span>
          </div>
        ))}
        <div className="flex items-center justify-between border-t border-linha pt-2 text-sm font-semibold">
          <span>Híbrido contra puro no ano</span>
          <span className={`num ${h.diferenca > 0 ? 'text-ok' : 'text-alerta'}`}>{sinal(h.diferenca)}</span>
        </div>
      </div>
    </Cartao>
  )
}

type MesHibrido = NonNullable<ResultadoHibrido['porCompetencia']>[number]

function MesAMes({ h }: { h: ResultadoHibrido }) {
  const ms = h.porCompetencia!
  const paga = (m: MesHibrido) => m.dasHibrido + m.debitoCbs - m.creditoCbs + m.debitoIbs - m.creditoIbs
  const soma = (f: (m: MesHibrido) => number) => ms.reduce((a, m) => a + f(m), 0)
  /** célula sem "R$" (a legenda diz que é em reais): cabe sem quebrar linha */
  const valor = (c: number) => reais(c).replace('R$ ', '')
  const colunas: Array<[string, (m: MesHibrido) => number]> = [
    ['Entradas', (m) => m.compras],
    ['Saídas', (m) => m.vendas],
    ['DAS puro', (m) => m.dasPuro],
    ['DAS híbrido', (m) => m.dasHibrido],
    ['Débito CBS', (m) => m.debitoCbs],
    ['Débito IBS', (m) => m.debitoIbs],
    ['Crédito CBS', (m) => m.creditoCbs],
    ['Crédito IBS', (m) => m.creditoIbs],
    ['Híbrido paga', paga],
  ]
  return (
    <div className="flex flex-col gap-2">
      <div className="overflow-x-auto rounded-md border border-linha">
        <table className="w-full min-w-[60rem] border-collapse whitespace-nowrap text-[13px]">
          <thead>
            <tr className="bg-fundo text-[11px] uppercase tracking-[0.05em] text-tinta-3">
              <th rowSpan={2} className="sticky left-0 z-10 border-b border-r border-linha bg-fundo px-3 py-2 text-left font-medium">
                Mês
              </th>
              <th colSpan={2} className="border-b border-r border-linha px-3 py-1.5 text-center font-medium">
                Notas
              </th>
              <th className="border-b border-r border-linha px-3 py-1.5 text-center font-medium">Puro</th>
              <th colSpan={6} className="border-b border-linha px-3 py-1.5 text-center font-medium">
                Híbrido
              </th>
            </tr>
            <tr className="bg-fundo text-xs text-tinta-2">
              {colunas.map(([c], i) => (
                <th key={c} className={`border-b border-linha px-3 py-2 text-right font-medium ${[1, 2].includes(i) ? 'border-r' : ''} ${i === colunas.length - 1 ? 'text-navy' : ''}`}>
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ms.map((m, k) => (
              <tr key={m.competencia} className={`${k % 2 ? 'bg-fundo/60' : 'bg-painel'} hover:bg-navy/[0.04]`}>
                <td className={`sticky left-0 border-b border-r border-linha px-3 py-1.5 font-medium ${k % 2 ? 'bg-fundo' : 'bg-painel'}`}>{mesAno(m.competencia)}</td>
                {colunas.map(([c, f], i) => (
                  <td key={c} className={`num border-b border-linha px-3 py-1.5 text-right ${[1, 2].includes(i) ? 'border-r' : ''} ${i === colunas.length - 1 ? 'font-semibold text-navy' : ''}`}>
                    {valor(f(m))}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="bg-navy/[0.06] font-semibold">
              <td className="sticky left-0 border-r border-t-2 border-linha bg-painel px-3 py-2">Total</td>
              {colunas.map(([c, f], i) => (
                <td key={c} className={`num border-t-2 border-linha px-3 py-2 text-right ${[1, 2].includes(i) ? 'border-r' : ''} ${i === colunas.length - 1 ? 'text-navy' : ''}`}>
                  {valor(soma(f))}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-xs text-tinta-3">
        Valores em reais. Meses reais do ano-base, sem projeção. No híbrido: DAS sem a parcela de CBS/IBS + débitos das vendas (a preço de equilíbrio) − créditos das compras. Mês com crédito maior que o
        débito gera saldo credor.
      </p>
    </div>
  )
}

function Trajetoria({ anos }: { anos: ResultadoHibrido[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[44rem] text-sm">
        <thead>
          <tr className="text-xs text-tinta-3">
            <th className="pb-2 text-left font-medium">Ano</th>
            <th className="pb-2 text-right font-medium">Puro: DAS</th>
            <th className="pb-2 text-right font-medium">Híbrido: DAS</th>
            <th className="pb-2 text-right font-medium">Híbrido: CBS + IBS</th>
            <th className="pb-2 text-right font-medium">Híbrido: guias</th>
            <th className="pb-2 text-right font-medium">Híbrido × puro</th>
            <th className="pb-2 text-right font-medium">Faixa (otimista / conservador)</th>
            <th className="pb-2 text-right font-medium">Indica</th>
          </tr>
        </thead>
        <tbody>
          {anos.map((a) => (
            <tr key={a.ano} className="border-t border-linha">
              <td className="py-1.5 font-medium">{a.ano}</td>
              <td className="num py-1.5 text-right">{reaisCompacto(a.aPagar!.puro.total)}</td>
              <td className="num py-1.5 text-right">{reaisCompacto(a.aPagar!.hibrido.das)}</td>
              <td className="num py-1.5 text-right">{reaisCompacto(a.aPagar!.hibrido.cbs + a.aPagar!.hibrido.ibs)}</td>
              <td className="num py-1.5 text-right">{reaisCompacto(a.aPagar!.hibrido.total)}</td>
              <td className={`num py-1.5 text-right ${a.diferenca > 0 ? 'text-ok' : 'text-alerta'}`}>{sinal(a.diferenca)}</td>
              <td className="num py-1.5 text-right text-tinta-2">{a.faixa ? `${reaisCompacto(a.faixa.otimista.diferenca)} / ${reaisCompacto(a.faixa.conservador.diferenca)}` : '—'}</td>
              <td className="py-1.5 text-right">{a.recomendacao === 'HIBRIDO' ? 'híbrido' : 'puro'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const ROTULO_REPASSE = (repasse: number, credita: boolean) =>
  repasse === 0 ? (credita ? 'Cliente paga o mesmo (preço cai)' : 'Consumidor paga o mesmo (preço cai)') : repasse === 1_000_000 ? 'Mantém o preço e soma o tributo' : `Repasse de ${pct(repasse, 0)}`

/** Como fica o preço de uma venda de R$ 10.000,00 no híbrido, por tipo de cliente, e a conta do ano em cada política. */
function PrecoDeVenda({ h }: { h: ResultadoHibrido }) {
  const linhas: Array<[string, (x: ExemploVenda) => number, boolean?]> = [
    ['Preço do produto', (x) => x.preco],
    ['IBS/CBS por fora', (x) => x.tributo],
    ['Total da nota', (x) => x.nota, true],
    ['Cliente credita', (x) => x.creditoCliente],
    ['Custo real do cliente', (x) => x.custoCliente, true],
    ['DAS da empresa', (x) => x.das],
    ['Fica para a empresa', (x) => x.fica, true],
  ]
  return (
    <Cartao titulo="Preço de venda no híbrido: uma venda de R$ 10.000,00">
      <p className="mb-3 text-sm text-tinta-2">
        No puro nada muda na nota. No híbrido ela passa a ter o preço do produto e o IBS/CBS ({pct(h.aliquotas.cbs + h.aliquotas.ibs)}) por fora — dá para baixar o preço para o cliente pagar o
        mesmo, ou manter o preço e ele pagar o tributo. Empresa que se credita recupera o tributo; consumidor não.
      </p>
      <div className="flex flex-col gap-5">
        {h.exemploPreco!.map((g) => {
          const credita = g.grupo === 'COM_CREDITO'
          const colunas = [
            { titulo: 'Hoje (puro)', x: g.hoje, destaque: false },
            ...g.hibrido.map((x) => ({ titulo: `Híbrido: ${ROTULO_REPASSE(x.repasse, credita).toLowerCase()}`, x, destaque: x.repasse === h.repasseUsado?.[g.grupo] })),
          ]
          return (
            <div key={g.grupo}>
              <p className="mb-2 text-sm font-medium">{credita ? 'Cliente empresa do regime regular (aproveita crédito)' : 'Consumidor final e empresa sem crédito'}</p>
              <div className={`grid gap-3 sm:grid-cols-2 ${colunas.length > 3 ? '2xl:grid-cols-4' : 'xl:grid-cols-3'}`}>
                {colunas.map((c) => (
                  <Painel
                    key={c.titulo}
                    titulo={c.titulo}
                    subtitulo={c.destaque ? 'forma de preço usada na simulação' : undefined}
                    destaque={c.destaque}
                    linhas={linhas.slice(0, -1).map(([rotulo, f, forte]) => ({ rotulo, valor: f(c.x), forte }))}
                    total={c.x.fica}
                  />
                ))}
              </div>
            </div>
          )
        })}
      </div>
      <p className="mt-4 text-sm font-medium">E no ano, com todas as vendas, conforme a política de preço:</p>
      <table className="mt-1 w-full text-sm">
        <tbody>
          {h.politicasPreco!.map((x) => (
            <tr key={x.politica} className="border-t border-linha">
              <td className="py-1.5">{ROTULO_POLITICA_PRECO[x.politica]}</td>
              <td className="num py-1.5 text-right text-tinta-2">guias {reaisCompacto(x.guiasHibrido)}</td>
              <td className={`num py-1.5 text-right font-semibold ${x.diferenca > 0 ? 'text-ok' : 'text-alerta'}`}>{sinal(x.diferenca)}</td>
              <td className="py-1.5 pl-3 text-right">
                <Selo tom={x.recomendacao === 'HIBRIDO' ? 'navy' : 'neutro'}>{x.recomendacao === 'HIBRIDO' ? 'híbrido' : 'puro'}</Selo>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-2 text-xs text-tinta-3">
        Valores = quanto o híbrido deixa a mais (+) ou a menos (−) que o puro, com o mesmo volume de vendas. Subir o preço para o consumidor pode custar vendas — isso não está no número.
      </p>
    </Cartao>
  )
}
